import { type FormEvent, useEffect, useState } from 'react';
import {
    agentsApi,
    agentRolesApi,
    type Agent,
    type AgentsResponse,
    type CreateRolePayload,
    type Role,
    type RolesPermissionsResponse,
    type RolesResponse,
} from '../utils/apiClient';

const RoleManagement = () => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [createSuccess, setCreateSuccess] = useState<string | null>(null);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [permissionsLoading, setPermissionsLoading] = useState(false);
    const [permissionsError, setPermissionsError] = useState<string | null>(null);
    const [formState, setFormState] = useState<CreateRolePayload>({
        name: '',
        description: '',
        isDefault: false,
    });
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [agentsError, setAgentsError] = useState<string | null>(null);
    const [selectedAgentId, setSelectedAgentId] = useState<string>('');
    const [assigning, setAssigning] = useState(false);
    const [assignError, setAssignError] = useState<string | null>(null);
    const [assignSuccess, setAssignSuccess] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        const loadRoles = async () => {
            setLoading(true);
            setError(null);
            const result = await agentRolesApi.list();
            if (!isMounted) {
                return;
            }
            setLoading(false);
            if (!result.success) {
                setError(result.error);
                return;
            }
            const payload: RolesResponse = result.data;
            setRoles(Array.isArray(payload.data) ? payload.data : []);
        };

        loadRoles();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const loadPermissions = async () => {
            if (!showCreate) {
                return;
            }
            setPermissionsLoading(true);
            setPermissionsError(null);
            const result = await agentRolesApi.permissions();
            if (!isMounted) {
                return;
            }
            setPermissionsLoading(false);
            if (!result.success) {
                setPermissionsError(result.error);
                return;
            }
            const payload: RolesPermissionsResponse = result.data;
            setPermissions(Array.isArray(payload.data) ? payload.data : []);
        };

        loadPermissions();
        return () => {
            isMounted = false;
        };
    }, [showCreate]);

    const handleToggleCreate = () => {
        setShowCreate((prev) => !prev);
        setCreateError(null);
        setCreateSuccess(null);
    };

    const closeAssignModal = () => {
        setShowAssignModal(false);
        setSelectedRole(null);
        setAgents([]);
        setAgentsError(null);
        setAgentsLoading(false);
        setSelectedAgentId('');
        setAssignError(null);
        setAssignSuccess(null);
    };

    const openAssignModal = async (role: Role) => {
        setSelectedRole(role);
        setShowAssignModal(true);
        setAgents([]);
        setAgentsError(null);
        setAssignError(null);
        setAssignSuccess(null);
        setSelectedAgentId('');
        setAgentsLoading(true);

        const result = await agentsApi.list();
        setAgentsLoading(false);
        if (!result.success) {
            setAgentsError(result.error);
            return;
        }
        const payload: AgentsResponse = result.data;
        setAgents(Array.isArray(payload.data) ? payload.data : []);
    };

    const togglePermission = (permission: string) => {
        setSelectedPermissions((prev) =>
            prev.includes(permission) ? prev.filter((item) => item !== permission) : [...prev, permission],
        );
    };

    const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setCreateError(null);
        setCreateSuccess(null);
        setCreating(true);

        const payload: CreateRolePayload = {
            name: formState.name.trim(),
            description: formState.description?.trim() || undefined,
            permissions: selectedPermissions,
            isDefault: formState.isDefault ?? false,
        };

        const result = await agentRolesApi.create(payload);
        setCreating(false);

        if (!result.success) {
            setCreateError(result.error);
            return;
        }

        setRoles((prev) => [result.data, ...prev]);
        setCreateSuccess('Role created successfully.');
        setFormState({
            name: '',
            description: '',
            isDefault: false,
        });
        setSelectedPermissions([]);
        setShowCreate(false);
    };

    const handleAssign = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!selectedRole || !selectedAgentId) {
            return;
        }
        setAssignError(null);
        setAssignSuccess(null);
        setAssigning(true);
        const result = await agentRolesApi.assign(selectedRole.id, selectedAgentId);
        setAssigning(false);
        if (!result.success) {
            setAssignError(result.error);
            return;
        }
        setAssignSuccess('Agent assigned to role.');
    };

    return (
        <div className="px-6 py-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
                <h1 className="text-2xl font-semibold text-slate-900">Roles</h1>
                <button
                    type="button"
                    onClick={handleToggleCreate}
                    className="inline-flex items-center justify-center rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500"
                >
                    {showCreate ? 'Cancel' : 'Create Role'}
                </button>
            </div>
            {showCreate && (
                <form onSubmit={handleCreate} className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-slate-700" htmlFor="role-name">
                                Role name
                            </label>
                            <input
                                id="role-name"
                                value={formState.name}
                                onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900"
                                placeholder="SupportAgent"
                                required
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-slate-700" htmlFor="role-description">
                                Description
                            </label>
                            <input
                                id="role-description"
                                value={formState.description ?? ''}
                                onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900"
                                placeholder="Handles customer chats and tickets"
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <p className="text-sm font-medium text-slate-700">Permissions</p>
                            {permissionsLoading && <p className="text-sm text-slate-500">Loading permissions...</p>}
                            {!permissionsLoading && permissionsError && <p className="text-sm text-rose-600">{permissionsError}</p>}
                            {!permissionsLoading && !permissionsError && permissions.length === 0 && (
                                <p className="text-sm text-slate-500">No permissions available.</p>
                            )}
                            {!permissionsLoading && !permissionsError && permissions.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {permissions.map((permission) => (
                                        <label
                                            key={permission}
                                            className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
                                                selectedPermissions.includes(permission)
                                                    ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                                                    : 'border-slate-200 bg-white text-slate-600'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedPermissions.includes(permission)}
                                                onChange={() => togglePermission(permission)}
                                                className="h-3 w-3 rounded border-slate-300"
                                            />
                                            {permission}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                checked={formState.isDefault ?? false}
                                onChange={(event) => setFormState((prev) => ({ ...prev, isDefault: event.target.checked }))}
                                className="h-4 w-4 rounded border-slate-300"
                            />
                            Set as default role
                        </label>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        <button
                            type="submit"
                            disabled={creating}
                            className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {creating ? 'Creating...' : 'Save Role'}
                        </button>
                        {createError && <p className="text-sm text-rose-600">{createError}</p>}
                        {createSuccess && <p className="text-sm text-emerald-600">{createSuccess}</p>}
                    </div>
                </form>
            )}
            {loading && <p className="text-sm text-slate-500">Loading roles...</p>}
            {!loading && error && <p className="text-sm text-rose-600">{error}</p>}
            {!loading && !error && roles.length === 0 && (
                <p className="text-sm text-slate-500">No roles found.</p>
            )}
            {!loading && !error && roles.length > 0 && (
                <ul className="space-y-2">
                    {roles.map((role) => (
                        <li key={role.id}>
                            <button
                                type="button"
                                onClick={() => openAssignModal(role)}
                                className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50"
                            >
                                {role.name}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {showAssignModal && selectedRole && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Assign Agent</h2>
                                <p className="text-sm text-slate-500">Role: {selectedRole.name}</p>
                            </div>
                            <button
                                type="button"
                                onClick={closeAssignModal}
                                className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-100"
                            >
                                Close
                            </button>
                        </div>
                        <form onSubmit={handleAssign} className="mt-4 space-y-4">
                            {agentsLoading && <p className="text-sm text-slate-500">Loading agents...</p>}
                            {!agentsLoading && agentsError && <p className="text-sm text-rose-600">{agentsError}</p>}
                            {!agentsLoading && !agentsError && agents.length === 0 && (
                                <p className="text-sm text-slate-500">No agents available.</p>
                            )}
                            {!agentsLoading && !agentsError && agents.length > 0 && (
                                <div className="max-h-64 space-y-2 overflow-y-auto pr-2">
                                    {agents.map((agent) => (
                                        <label
                                            key={agent.id}
                                            className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm ${
                                                selectedAgentId === agent.id
                                                    ? 'border-emerald-300 bg-emerald-50'
                                                    : 'border-slate-200'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="radio"
                                                    name="agent"
                                                    value={agent.id}
                                                    checked={selectedAgentId === agent.id}
                                                    onChange={() => setSelectedAgentId(agent.id)}
                                                    className="h-4 w-4"
                                                />
                                                <div>
                                                    <p className="text-slate-900">{agent.name ?? 'Unnamed agent'}</p>
                                                    <p className="text-xs text-slate-500">{agent.email ?? agent.id}</p>
                                                </div>
                                            </div>
                                            {agent.isActive === false && (
                                                <span className="rounded-full bg-amber-100 px-2 py-1 text-[0.65rem] text-amber-700">
                                                    Inactive
                                                </span>
                                            )}
                                        </label>
                                    ))}
                                </div>
                            )}
                            <div className="flex flex-wrap items-center gap-3">
                                <button
                                    type="submit"
                                    disabled={assigning || !selectedAgentId}
                                    className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {assigning ? 'Assigning...' : 'Assign Agent'}
                                </button>
                                {assignError && <p className="text-sm text-rose-600">{assignError}</p>}
                                {assignSuccess && <p className="text-sm text-emerald-600">{assignSuccess}</p>}
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoleManagement;
